/**
 * API request utilities
 */

const requestCache = new Map<string, Promise<any>>();

/**
 * A utility to prevent duplicate in-flight requests.
 * If a request with the same key is already in progress, it returns the existing promise.
 * @param key A unique key for the request (e.g., 'GET-/v1/data').
 * @param requestFn The function that returns the promise of the request.
 * @returns The promise of the API request.
 * @example
 * const data = await deduplicatedRequest(
 *   'GET-/api/users',
 *   () => fetch('/api/users').then(r => r.json())
 * );
 */
export const deduplicatedRequest = async <T>(
  key: string,
  requestFn: () => Promise<T>
): Promise<T> => {
  // If request is already in flight, return the existing promise
  if (requestCache.has(key)) {
    return requestCache.get(key) as Promise<T>;
  }

  const promise = requestFn();
  requestCache.set(key, promise);

  // Clean up cache when request completes, regardless of success or failure
  promise.finally(() => {
    requestCache.delete(key);
  });

  return promise;
};